/**
 * Loads the merchant API `/flags` list and merges it over `defaults`.
 *
 * Flag names arrive in snake_case (`openpayd_integration`) and are exposed in
 * camelCase (`openpaydIntegration`) so callers can type them as plain object
 * keys. Any flag missing from the response keeps its default, so a caller
 * that defaults to `undefined` can tell "off" apart from "unknown".
 *
 * Throws on network or non-2xx errors; callers decide how to report and what
 * to fall back to.
 */
export type FeatureFlagsShape<T> = { [K in keyof T]: boolean | undefined };

interface FeatureFlagsResponse {
  data: { attributes: { name: string; enabled: boolean } }[];
}

export const snakeToCamel = (name: string): string =>
  name.replace(/_(\w)/g, (_, letter: string) => letter.toUpperCase());

export default async function fetchFeatureFlags<T extends FeatureFlagsShape<T>>(
  merchantApiUrl: string,
  defaults: T
): Promise<T> {
  const response = await fetch(`${merchantApiUrl}/flags`);

  if (!response.ok) {
    throw new Error(`Failed to fetch feature flags: ${response.status}`);
  }

  const { data } = (await response.json()) as FeatureFlagsResponse;

  return data.reduce<T>(
    (flags, { attributes }) => ({
      ...flags,
      [snakeToCamel(attributes.name)]: attributes.enabled,
    }),
    { ...defaults }
  );
}
